import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  TextField,
  Typography
} from '@mui/material'
import React from 'react'
import { connect } from 'react-redux'
import NivosModal from 'components/nivos-modal'
import NivosRTE from 'components/nivos-rte'
import {
  Operations as AmenityOperations,
  operations as amenityOperations
} from 'states/dux/amenity/operations'
import { Payload as AmenityPayload } from 'states/dux/amenity/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import {
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  amenity: AmenityPayload
  user: UserPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    amenity: AmenityOperations
    user: UserOperations
  }
}

interface OwnProps {
  open: boolean
  setOpen: (_: boolean) => void
}

type AppProps = StateProps & DispatchProps & OwnProps

const CreateAmenityFC: React.FC<any> = (props: AppProps) => {
  const { open, setOpen, user, operations } = props
  const [name, setName] = React.useState('')
  const [images, setImages] = React.useState<FileList>()
  const [description, setDescription] = React.useState('')
  const [rte, setRte] = React.useState('')
  const [formLoading, setFormLoading] = React.useState(false)
  const [bookable, setBookable] = React.useState(true)

  const defaultErrorsState = {
    name: false,
    description: false,
    images: false
  }

  const [errors, setErrors] = React.useState(defaultErrorsState)

  const generateFormData = (): FormData => {
    const formData = new FormData()
    formData.append('name', name)
    formData.append('description', description)
    formData.append('description_rte', rte)
    formData.append('bookable', bookable.toString())
    formData.append('updated_by', user.id.toString())
    formData.append('created_by', user.id.toString())

    if (images !== undefined) {
      for (let i = 0; i < images.length; i++) {
        formData.append('amenity_images', images[i], images[i].name)
      }
    }

    return formData
  }

  const clearForm = (): void => {
    setName('')
    setDescription('')
    setRte('')
    setImages(undefined)
    setBookable(false)
    setErrors(defaultErrorsState)
  }

  const validateInputs = (): void => {
    let errorState = { ...errors }
    let hasError = false
    const requiredInputs = [
      { label: 'name', val: name },
      { label: 'description', val: description },
      { label: 'images', val: images }
    ]

    setFormLoading(true)

    // Validate required fields
    requiredInputs.forEach(input => {
      if (input.val === null || input.val === '' || input.val === undefined) {
        errorState = { ...errorState, [input.label]: true }
        hasError = true
      } else {
        errorState = { ...errorState, [input.label]: false }
      }
    })

    setErrors({ ...errorState })

    if (hasError) {
      setFormLoading(false)
      return
    }

    operations.amenity.loading(true)
    void post('/booking/items/amenity/', generateFormData(), true).then(
      data => {
        operations.amenity.add(objectifyDateStringFields(data))
        clearForm()
        setFormLoading(false)
        operations.amenity.loading(false)
        setOpen(false)
      }
    )
  }

  return (
    <NivosModal title={'Add Amenity'} open={open} setOpen={setOpen}>
      <Box display={'flex'} flexDirection={'column'} minWidth={700} padding={2}>
        <Box display={'flex'} alignItems={'center'} padding={1}>
          <Typography variant={'h6'} flexGrow={1} width={0.5}>
            Name:{' '}
          </Typography>
          <TextField
            error={errors.name}
            type={'text'}
            sx={{ width: 0.5 }}
            value={name}
            onChange={event => {
              setName(event.target.value)
            }}
          />
        </Box>
        <Box display={'flex'} alignItems={'flex-start'} padding={1}>
          <Typography variant={'h6'} flexGrow={1} width={0.5}>
            Description:{' '}
          </Typography>
          <Box
            width={0.5}
            border={errors.description ? '1px solid red' : 'none'}
          >
            <NivosRTE
              rteState={rte}
              setRteState={setRte}
              setPLainState={setDescription}
            />
          </Box>
        </Box>
        <Box display={'flex'} alignItems={'flex-start'} padding={1}>
          <Typography variant={'h6'} flexGrow={1} width={0.5}>
            Bookable:
          </Typography>
          <Box width={0.5}>
            <Checkbox
              checked={bookable}
              onChange={(event, checked) => setBookable(checked)}
            />
          </Box>
        </Box>
        <Box display={'flex'} alignItems={'center'} padding={1}>
          <Typography variant={'h6'} flexGrow={1} width={0.5}>
            Images:{' '}
          </Typography>
          <input
            style={{
              border: errors.images ? '1px solid red' : 'none',
              width: '50%',
              padding: '2px',
              borderRadius: '4px'
            }}
            type={'file'}
            accept={'image/*'}
            multiple={true}
            onChange={event => {
              if (event.currentTarget.files != null) {
                setImages(event.currentTarget.files)
              }
            }}
          />
        </Box>
        <Box display={'flex'} justifyContent={'flex-end'} padding={1}>
          <Button
            variant={'contained'}
            color={'primary'}
            size={'large'}
            onClick={() => validateInputs()}
            disabled={formLoading}
          >
            {formLoading ? <CircularProgress /> : 'Add Amenity'}
          </Button>
        </Box>
      </Box>
    </NivosModal>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    amenity: state.amenity,
    user: state.user
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  amenity: amenityOperations,
  user: userOperations
})

const CreateAmenity = connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateAmenityFC) as React.FC<OwnProps>

export default CreateAmenity
